import React, { useRef, useState } from "react";
import "../lib/amplify";
import { AlertCircle, Eye, EyeOff, Lock, User } from "lucide-react";
import { signIn, signOut } from "@aws-amplify/auth";
import { authFieldClassName, authFormClassName, authHeaderClassName, authInputClassName, authLabelClassName, authPrimaryButtonClassName, authSecondaryActionClassName, authSecondaryActionContainerClassName, authSubtitleClassName, authTitleClassName, authValidationAreaClassName } from "./authFormStyles";
import { translateAuthError } from "./authErrors";

interface LoginFormProps { onForgotPassword: () => void; onLoginSuccess?: () => Promise<void> | void; initialError?: string | null; }

export const LoginForm: React.FC<LoginFormProps> = ({ onForgotPassword, onLoginSuccess, initialError }) => {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [touchedFields, setTouchedFields] = useState({ login: false, password: false });
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const loginInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const isLoginValid = login.includes("@") ? login.includes(".") && login.length > 5 : login.trim().length >= 3;
  const isPasswordValid = password.length >= 8;
  const showLoginError = (touchedFields.login || hasSubmitted) && !isLoginValid;
  const showPasswordError = (touchedFields.password || hasSubmitted) && !isPasswordValid;
  const loginState = showLoginError ? "invalid" : "default";
  const passwordState = showPasswordError ? "invalid" : "default";

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setHasSubmitted(true);
    if (!isLoginValid || !isPasswordValid) {
      if (!isLoginValid) loginInputRef.current?.focus();
      else passwordInputRef.current?.focus();
      return;
    }
    setIsLoading(true); setErrorMessage(null);
    try {
      await signOut();
      const username = login.includes("@") ? login.trim() : `${login.trim()}@diariabrasil.com.br`;
      await signIn({ username, password });
      await onLoginSuccess?.();
    } catch (error: any) {
      setErrorMessage(
        translateAuthError(error, "Não foi possível entrar. Tente novamente."),
      );
    }
    finally { setIsLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit} noValidate className={authFormClassName}>
      <div className={authHeaderClassName}>
        <h1 className={authTitleClassName}>Acesse sua conta</h1>
        <p className={authSubtitleClassName}>Entre com seu usuário ou e-mail e senha.</p>
      </div>
      {(initialError || errorMessage) && <div role="alert" className="rounded-control border border-red-200 bg-red-50 p-3 text-sm text-red-700">{errorMessage || initialError}</div>}
      <div className={authFieldClassName}>
        <label htmlFor="login" className={authLabelClassName}>Usuário ou e-mail</label>
        <div className="relative">
          <User aria-hidden="true" size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-foreground-muted" />
          <input ref={loginInputRef} type="text" id="login" value={login} onChange={(event) => setLogin(event.target.value)} onBlur={() => setTouchedFields((fields) => ({ ...fields, login: true }))} autoComplete="username" className={authInputClassName(loginState)} placeholder="seu usuário ou e-mail" required disabled={isLoading} aria-invalid={showLoginError || undefined} aria-describedby={showLoginError ? "login-error" : undefined} />
          <span className="pointer-events-none absolute right-3 top-1/2 flex h-[18px] w-[18px] -translate-y-1/2 items-center justify-center">
            {loginState === "invalid" && <AlertCircle aria-label="Login inválido" size={18} className="text-red-500" />}
          </span>
        </div>
        <div className={authValidationAreaClassName} aria-live="polite">{showLoginError && <p id="login-error" className="text-xs leading-5 text-red-600">Informe um usuário ou e-mail válido.</p>}</div>
      </div>
      <div className={authFieldClassName}>
        <label htmlFor="password" className={authLabelClassName}>Senha</label>
        <div className="relative">
          <Lock aria-hidden="true" size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-foreground-muted" />
          <input ref={passwordInputRef} type={showPassword ? "text" : "password"} id="password" value={password} onChange={(event) => setPassword(event.target.value)} onBlur={() => setTouchedFields((fields) => ({ ...fields, password: true }))} autoComplete="current-password" className={authInputClassName(passwordState)} placeholder="Mínimo 8 caracteres" required disabled={isLoading} aria-invalid={showPasswordError || undefined} aria-describedby={showPasswordError ? "password-error" : undefined} />
          <button type="button" onClick={() => setShowPassword(!showPassword)} disabled={isLoading} aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"} className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center text-foreground-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-focus/30 disabled:cursor-not-allowed disabled:text-slate-400">
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        <div className={authValidationAreaClassName} aria-live="polite">{showPasswordError && <p id="password-error" className="text-xs leading-5 text-red-600">Use pelo menos 8 caracteres.</p>}</div>
      </div>
      <button type="submit" disabled={isLoading} className={authPrimaryButtonClassName(isLoading)}>
        {isLoading ? "Verificando credenciais..." : "Entrar"}
      </button>
      <div className={authSecondaryActionContainerClassName}>
        <button type="button" onClick={onForgotPassword} className={authSecondaryActionClassName}>Esqueci minha senha</button>
      </div>
    </form>
  );
};
